import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  ElementRef,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectOption {
  label: string;
  value: any;
  disabled?: boolean;
}

@Component({
  selector: 'app-select',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiSelect),
      multi: true,
    },
  ],
  template: `
    <div class="relative w-full" [class]="styleClass()">
      <button
        type="button"
        [disabled]="disabled()"
        [class]="triggerClasses()"
        (click)="toggle($event)"
        (keydown)="onKeydown($event)"
        [attr.aria-expanded]="isOpen()"
        [attr.aria-haspopup]="'listbox'"
        [attr.aria-label]="placeholder()"
        [attr.aria-disabled]="disabled()"
      >
        <span class="flex-1 text-left truncate" [class.text-gray-400]="!selectedOption()">
          {{ selectedOption()?.label || placeholder() }}
        </span>
        @if (showClear() && selectedOption()) {
          <button
            type="button"
            class="shrink-0 p-0.5 text-gray-400 hover:text-gray-600 focus:outline-none rounded"
            (click)="clear($event)"
            aria-label="Limpiar selección"
          >
            <i class="bi bi-x" aria-hidden="true"></i>
          </button>
        }
        <i
          class="bi bi-chevron-down shrink-0 text-gray-400 transition-transform duration-200"
          [class.rotate-180]="isOpen()"
          aria-hidden="true"
        ></i>
      </button>

      @if (isOpen()) {
        <div
          class="fixed z-[10000] bg-white border border-slate-200 rounded-xl shadow-xl overflow-auto py-1 animate-fade-in"
          [style.top.px]="dropdownTop()"
          [style.bottom.px]="dropdownBottom()"
          [style.left.px]="dropdownLeft()"
          [style.minWidth.px]="dropdownWidth()"
          [style.maxHeight.px]="dropdownMaxHeight()"
          [attr.role]="'listbox'"
          [attr.aria-label]="placeholder()"
        >
          @if (filter()) {
            <div class="px-3 py-2 border-b border-slate-100 sticky top-0 bg-white z-10">
              <div class="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 focus-within:ring-2 focus-within:ring-accent-500/20 focus-within:border-accent-400 transition-all">
                <i class="bi bi-search text-xs text-slate-400 shrink-0"></i>
                <input
                  type="text"
                  class="flex-1 border-0 bg-transparent text-sm focus:outline-none placeholder:text-slate-400"
                  placeholder="Filtrar..."
                  [value]="filterText()"
                  (input)="onFilterInput($event)"
                  (click)="$event.stopPropagation()"
                />
                @if (filterText()) {
                  <button type="button" class="shrink-0 text-slate-400 hover:text-slate-600 p-0.5" (click)="filterText.set(''); $event.stopPropagation()" aria-label="Limpiar filtro">
                    <i class="bi bi-x text-sm"></i>
                  </button>
                }
              </div>
            </div>
          }

          @if (loading()) {
            <div class="flex items-center justify-center gap-2 px-4 py-8 text-gray-400 text-sm">
              <div class="w-4 h-4 border-2 border-gray-300 border-t-accent-600 rounded-full animate-spin"></div>
              Cargando...
            </div>
          } @else if (filteredOptions().length === 0) {
            <div class="px-4 py-8 text-sm text-slate-400 text-center">
              <i class="bi bi-inbox text-2xl block mb-2 opacity-40"></i>
              {{ emptyFilterMessage() }}
            </div>
          } @else {
            @for (option of filteredOptions(); track option.label + '_' + $index) {
              <button
                type="button"
                [class]="optionClasses(option)"
                [attr.role]="'option'"
                [attr.aria-selected]="isSelected(option)"
                [attr.aria-disabled]="!!option.disabled"
                [disabled]="option.disabled"
                (click)="select(option)"
              >
                <span class="truncate">{{ option.label }}</span>
                @if (isSelected(option)) {
                  <i class="bi bi-check2 shrink-0 text-accent-600 ml-3" aria-hidden="true"></i>
                }
              </button>
            }
          }
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UiSelect implements ControlValueAccessor {
  readonly options = input<SelectOption[]>([]);
  readonly placeholder = input<string>('Seleccionar');
  readonly disabled = input<boolean>(false);
  readonly filter = input<boolean>(false);
  readonly filterBy = input<string>('label');
  readonly showClear = input<boolean>(false);
  readonly emptyFilterMessage = input<string>('Sin resultados');
  readonly loading = input<boolean>(false);
  readonly styleClass = input<string>('');
  readonly compareWith = input<(a: any, b: any) => boolean>((a, b) => a === b);

  readonly value = model<any>(null);
  readonly onChange = output<any>();

  private cvaOnChange: (value: any) => void = () => {};
  private cvaOnTouched: () => void = () => {};

  protected readonly isOpen = signal(false);
  protected readonly filterText = signal('');
  protected readonly dropdownTop = signal<string | null>(null);
  protected readonly dropdownBottom = signal<string | null>(null);
  protected readonly dropdownLeft = signal(-9999);
  protected readonly dropdownWidth = signal(0);
  protected readonly dropdownMaxHeight = signal(256);

  private readonly elementRef = inject(ElementRef);

  private valuesEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    return this.compareWith()(a, b);
  }

  protected readonly selectedOption = computed(() => {
    const val = this.value();
    if (val == null) return null;
    return this.options().find((o) => this.valuesEqual(o.value, val)) || null;
  });

  protected readonly filteredOptions = computed(() => {
    const text = this.filterText().toLowerCase();
    if (!text) return this.options();
    const fields = this.filterBy().split(',').map((f) => f.trim());
    return this.options().filter((o) =>
      fields.some((field) => {
        const val = (o as any)[field];
        return val != null ? String(val).toLowerCase().includes(text) : false;
      }),
    );
  });

  protected triggerClasses(): string {
    return 'w-full flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-accent-500/20 focus:border-accent-400 bg-white text-left hover:border-slate-300 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed';
  }

  protected optionClasses(option: SelectOption): string {
    const selected = this.isSelected(option)
      ? 'bg-accent-50 text-accent-700 font-medium'
      : 'text-slate-700 hover:bg-slate-50';
    const disabledCls = option.disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer';
    return `w-full flex items-center justify-between px-3.5 py-2.5 text-sm text-left transition-colors duration-75 focus:outline-none focus:bg-slate-50 ${selected} ${disabledCls}`;
  }

  protected isSelected(option: SelectOption): boolean {
    return this.valuesEqual(this.value(), option.value);
  }

  toggle(event?: MouseEvent): void {
    if (this.disabled()) return;
    if (event) event.stopPropagation();
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  private open(): void {
    this.filterText.set('');
    this.isOpen.set(true);
    this.positionDropdown();
    document.addEventListener('click', this.onClickOutside, true);
    document.addEventListener('scroll', this.onScroll, true);
  }

  private positionDropdown(): void {
    const trigger = this.elementRef.nativeElement.querySelector('button') as HTMLElement;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;

    const gap = 4;
    const pad = 8;
    const maxH = 336;
    const viewportH = window.innerHeight;

    const spaceBelow = viewportH - rect.bottom - gap - pad;
    const spaceAbove = rect.top - gap - pad;
    const preferBelow = spaceBelow >= 200 || spaceBelow >= spaceAbove;

    if (preferBelow) {
      this.dropdownTop.set(`${rect.bottom + gap}`);
      this.dropdownBottom.set(null);
      this.dropdownMaxHeight.set(Math.min(maxH, spaceBelow));
    } else {
      this.dropdownTop.set(null);
      this.dropdownBottom.set(`${viewportH - rect.top + gap}`);
      this.dropdownMaxHeight.set(Math.min(maxH, spaceAbove));
    }

    this.dropdownLeft.set(Math.max(pad, rect.left));
    this.dropdownWidth.set(rect.width);
  }

  select(option: SelectOption): void {
    if (option.disabled) return;
    this.value.set(option.value);
    this.onChange.emit(option.value);
    this.cvaOnChange(option.value);
    this.close();
  }

  clear(event: MouseEvent): void {
    event.stopPropagation();
    this.value.set(null);
    this.onChange.emit(null);
    this.cvaOnChange(null);
  }

  close(): void {
    if (!this.isOpen()) return;
    document.removeEventListener('click', this.onClickOutside, true);
    document.removeEventListener('scroll', this.onScroll, true);
    this.isOpen.set(false);
    this.cvaOnTouched();
  }

  private readonly onScroll = (): void => {
    this.positionDropdown();
  };

  private readonly onClickOutside = (event: MouseEvent): void => {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  };

  protected onFilterInput(event: Event): void {
    this.filterText.set((event.target as HTMLInputElement).value);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') this.close();
  }

  writeValue(value: any): void {
    this.value.set(value ?? null);
  }
  registerOnChange(fn: (value: any) => void): void {
    this.cvaOnChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.cvaOnTouched = fn;
  }
  setDisabledState(): void {}
}
